/**
 * Figür-zemin: örtüşen şekiller ve gizli şekil.
 *
 * İkisi de aynı yetiyi çalıştırıyor — bütünün içinden parçayı
 * ayırmak — ama iki ayrı yoldan. Örtüşende parçalar birbirinin
 * üstünde ve hepsi aranıyor; gizlide tek bir parça var ve zemin onu
 * saklıyor.
 *
 * ÜRETİMİN ASIL İŞİ İKİSİNDE DE AYNI: cevabın tek olduğunu garanti
 * etmek. Üst üste binmiş şekillerde bir şekil ötekinin içine tamamen
 * düşerse "orada ne var" sorusunun cevabı yok; gizli şekil desende iki
 * kez bulunursa çocuğun işaretlediği ikinci kopya yanlış sayılır. İkisi
 * de sayfaya bakınca fark edilmiyor, üretimde engellenmesi gerekiyor.
 */

import { Rng } from '../../prng'
import { gradeSet } from '../helpers'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { ExerciseTemplate, RenderNode } from '../types'
import { SEKIL_ADI } from '../../render/shapes'
import type { ShapeName } from '../../render/shapes'

/**
 * Kontur şekil havuzu.
 *
 * Dokuzu da yalnızca çizgi: dolu bir şekil altındakini kapatıyor ve
 * örtüşme görevi "üstteki hangisi" oyununa dönüyor. Figür-zeminin
 * tamamı, çizgilerin birbirinin içinden geçmesinde.
 */
const KONTURLAR: ShapeName[] = [
  'daire_bos',
  'kare_bos',
  'ucgen_bos',
  'yildiz_bos',
  'elmas_bos',
  'kalp_bos',
  'altigen_bos',
  'besgen_bos',
  'arti_bos',
]

/* ================================================================== *
 * 28. Örtüşen şekiller (Poppelreuter mantığı)
 *
 * Birkaç kontur şekil üst üste biniyor; çocuk hangilerinin orada
 * olduğunu işaretliyor.
 *
 * ŞEKİLLER DÖNDÜRÜLMÜYOR ve bu bir eksiklik değil, bir zorunluluk:
 * içi boş bir KARE 45° döndürülünce içi boş bir ELMAS oluyor. İkisi de
 * havuzda olduğu için döndürme serbest bırakılsaydı aynı çizim iki
 * farklı şekil adıyla doğru olurdu ve cevap anahtarı ikisinden birini
 * yanlış sayardı. Zorluk döndürmeden değil, şekil sayısından ve
 * kümenin ne kadar sıkışık durduğundan geliyor.
 *
 * KÜMEDEKİ ŞEKİLLERİN HEPSİ AYNI BOYDA. Farklı boyda olsalardı küçük
 * olan büyüğün içine tamamen düşebilirdi; o şekli görmenin yolu
 * olmazdı ve sayfa cevaplanamaz hale gelirdi. Eşit boyda, merkezleri
 * ayrı iki şekilden hiçbiri ötekini içine alamıyor — bu geometrik bir
 * garanti, bir umut değil.
 * ================================================================== */
export const overlapFind: ExerciseTemplate = {
  code: 'overlap_find',
  title: 'Üst üste binmiş şekiller',
  domains: ['selective_attention', 'working_memory_visual'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Kümedeki şekil sayısı */
      sekil: scale(l, 2, 5),
      /** Merkezlerin en az uzaklığı: küçüldükçe örtüşme artıyor */
      aralik: Number((0.3 - l * 0.016).toFixed(3)),
      /** Kaç küme var */
      kume: l >= 6 ? 2 : 1,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const adet = p['sekil'] as number
    const aralik = p['aralik'] as number
    const kumeSayisi = p['kume'] as number

    const render: RenderNode[] = []
    const dogruIdler: string[] = []
    const anahtarSatiri: string[] = []

    for (let k = 0; k < kumeSayisi; k++) {
      const havuz = rng.shuffle(KONTURLAR)
      const varOlan = havuz.slice(0, adet)
      // En az iki çeldirici: tek çeldirici varsa "hepsini işaretle"
      // stratejisi neredeyse tam puan alıyor
      const celdirici = havuz.slice(adet, adet + Math.max(2, 9 - adet - 2))

      const merkezler = merkezDagit(rng, adet, aralik)
      render.push({
        t: 'scatter',
        overlap: true,
        height: 230,
        items: varOlan.map((s, i) => ({
          shape: s,
          x: merkezler[i]!.x,
          y: merkezler[i]!.y,
          // Hepsi aynı boyda: içine düşme olmasın
          size: 1.9,
        })),
      })

      const secenekler = rng.shuffle([...varOlan, ...celdirici])
      render.push({
        t: 'choices',
        items: secenekler.map((s, i) => ({ id: `k${k}_${i}`, shape: s })),
      })
      secenekler.forEach((s, i) => {
        if (varOlan.includes(s)) dogruIdler.push(`k${k}_${i}`)
      })
      anahtarSatiri.push(varOlan.map((s) => SEKIL_ADI[s]).join(', '))
    }

    return {
      code: 'overlap_find',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        kumeSayisi === 1
          ? 'Üst üste binmiş çizgilerin içinde hangi şekiller var? Aşağıdan hepsini işaretle.'
          : 'Her kümede hangi şekiller var? Her kümenin altından hepsini işaretle.',
      spokenPrompt:
        'Çizgiler birbirinin içinden geçiyor. Hangi şekillerin orada olduğunu bulup işaretleyeceksin.',
      render,
      solution: dogruIdler,
      grading: 'auto',
      estimatedSeconds: kumeSayisi * (30 + adet * 12),
      itemCount: dogruIdler.length,
    }
  },
  grade(instance, answer) {
    return gradeSet(instance.solution as string[], answer)
  },
  anahtar(instance) {
    const p = instance.params
    const sayi = p['kume'] as number
    const satirlar: string[] = []
    // Anahtar şekil ADLARIYLA yazılıyor: seçenek kimliği (`k0_3`)
    // uygulayıcının elindeki sayfada hiçbir yerde yazmıyor.
    const secenekDugumleri = (instance.render as RenderNode[]).filter((n) => n.t === 'choices')
    const dogru = new Set(instance.solution as string[])
    secenekDugumleri.forEach((n, i) => {
      if (n.t !== 'choices') return
      const adlar = n.items
        .filter((x) => dogru.has(x.id))
        .map((x) => SEKIL_ADI[x.shape as ShapeName])
      satirlar.push(sayi > 1 ? `${i + 1}. küme: ${adlar.join(', ')}` : adlar.join(', '))
    })
    return satirlar.join('   |   ')
  },
}

/**
 * Küme merkezleri: birbirine değecek kadar yakın, içine düşmeyecek
 * kadar ayrı.
 *
 * Aralık küçüldükçe örtüşme artıyor; sıfıra inemiyor çünkü aynı
 * merkezde iki şekil üst üste tam otururdu ve ikisi tek şekil gibi
 * okunurdu.
 */
function merkezDagit(rng: Rng, adet: number, aralik: number): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = []
  let guard = 0
  while (out.length < adet && guard++ < adet * 400) {
    const p = { x: 0.3 + rng.float() * 0.4, y: 0.3 + rng.float() * 0.4 }
    if (out.every((q) => Math.hypot(q.x - p.x, q.y - p.y) >= aralik)) out.push(p)
  }
  // Sığmadıysa çembere diz: küme her zaman üretilsin
  while (out.length < adet) {
    const i = out.length
    const a = (2 * Math.PI * i) / adet
    out.push({ x: 0.5 + 0.13 * Math.cos(a), y: 0.5 + 0.13 * Math.sin(a) })
  }
  return out
}

/* ================================================================== *
 * 29. Gizli şekil (Gottschaldt figürleri)
 *
 * Solda basit bir şekil, sağda karmaşık bir desen. Basit şekil desenin
 * içinde TAM BİR KEZ saklı; çocuk onu bulup üzerinden geçiyor.
 *
 * "Tam bir kez" bedava değil ve bu şablonun asıl işi o. Desen rastgele
 * çizgilerle doldurulsa, üç kenarlı bir hedef büyük ihtimalle birkaç
 * yerde birden oluşur; çocuk ikinci kopyayı işaretler ve yanlış
 * sayılır — oysa sayfada iki doğru cevap vardır. Üretim her çeldirici
 * kenarı eklemeden önce tekliği yeniden denetliyor ve bozan kenarı
 * atıyor.
 *
 * Teklik DÖNDÜRME VE AYNALAMAYA göre tanımlı: hedefin sekiz simetrisi
 * ve bütün ötelemeleri taranıyor. Daha dar bir tanım (yalnızca aynı
 * yönde) sayfayı yalancı yapardı — çocuk döndürülmüş kopyayı bulup
 * "ben buldum" der ve haklıdır.
 * ================================================================== */
export const hiddenFigure: ExerciseTemplate = {
  code: 'hidden_figure',
  title: 'Gizli şekli bul',
  domains: ['selective_attention', 'working_memory_visual'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Desen ızgarasının kenarı */
      n: l >= 8 ? 6 : l >= 4 ? 5 : 4,
      /** Hedefin kenar sayısı */
      hedef: scale(l, 3, 5),
      /** Desendeki toplam kenar sayısı */
      desen: scale(l, 8, 20),
      /** Çapraz kenarlar 3. düzeyden sonra */
      capraz: l >= 3,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const n = p['n'] as number
    const hedefKenar = p['hedef'] as number
    const desenKenar = p['desen'] as number
    const capraz = p['capraz'] as boolean

    const adimlar = yonler(capraz)

    let kurgu: { hedef: Nokta[]; yerlesik: string[]; desen: string[] } | null = null
    for (let deneme = 0; deneme < 60 && !kurgu; deneme++) {
      // 1) Hedef: küçük bir kutuda kırık çizgi
      const hedef = yolCiz(rng, 3, hedefKenar, adimlar)
      if (!hedef) continue

      // 2) Desene yerleştir: rastgele simetri ve öteleme
      const yerlesimler = tumYerlesimler(hedef, n)
      if (yerlesimler.length === 0) continue
      const secilen = rng.pick(yerlesimler)

      // 3) Çeldirici kenarlar: tekliği bozan eklenmiyor
      const desen = new Set(secilen)
      const adaylar = rng.shuffle(tumKenarlar(n, adimlar))
      for (const k of adaylar) {
        if (desen.size >= desenKenar) break
        if (desen.has(k)) continue
        desen.add(k)
        if (kacKez(hedef, n, desen) !== 1) desen.delete(k)
      }
      if (desen.size < Math.min(desenKenar, hedefKenar + 3)) continue
      if (kacKez(hedef, n, desen) !== 1) continue
      kurgu = { hedef, yerlesik: secilen, desen: [...desen] }
    }
    if (!kurgu) throw new Error('hidden_figure: kurgu bulunamadi')

    /**
     * Aranan şekil, desenle AYNI ızgarada çiziliyor.
     *
     * Önce kendi küçük kutusunda (sınırlayıcı kutusu kadar) çiziliyordu
     * ve iki ızgara aynı genişlikte basıldığı için nokta aralıkları
     * farklı çıkıyordu: aranan şekil desendekinden büyük görünüyordu.
     * Çocuk önce şekli zihninde küçültmek zorunda kalıyordu — görev
     * figür-zemin olmaktan çıkıp ölçek karşılaştırmaya dönüyordu.
     *
     * Sol üst köşeye yaslı çiziliyor: desendeki YERİNİ ele vermesin.
     */
    const hedefYolu = kurgu.hedef.map(([r, c]) => r * n + c)

    return {
      code: 'hidden_figure',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        'Soldaki şekil sağdaki desenin içinde bir kez saklı. Bul ve üzerinden geç. ' +
        'Şekil döndürülmüş ya da aynalanmış olabilir.',
      spokenPrompt:
        'Soldaki şekli sağdaki karışıklığın içinde arayacaksın. Yatmış ya da ters durmuş olabilir.',
      render: [
        { t: 'dots', n, model: hedefYolu, label: 'aranan şekil' },
        {
          t: 'dots',
          n,
          model: [],
          edges: kurgu.desen.map(kenarNoktalari),
          trace: true,
          label: 'desen',
        },
      ],
      solution: kurgu.yerlesik,
      grading: 'auto',
      estimatedSeconds: 40 + desenKenar * 6,
      itemCount: kurgu.yerlesik.length,
    }
  },
  /**
   * Puanlama kenar KÜMESİ üzerinde: çocuğun hangi uçtan başladığı ve
   * hangi yöne gittiği önemli değil, çizdiği şekil önemli.
   */
  grade(instance, answer) {
    const beklenen = instance.solution as string[]
    const yol = (Array.isArray(answer) ? answer : []).map(Number).filter((x) => !Number.isNaN(x))
    const verilen: string[] = []
    for (let i = 1; i < yol.length; i++) verilen.push(kenarAdi(yol[i - 1]!, yol[i]!))
    return gradeSet(beklenen, verilen)
  },
  anahtar(instance) {
    const n = instance.params['n'] as number
    const noktalar = new Set<number>()
    for (const k of instance.solution as string[]) {
      const [a, b] = k.split('-').map(Number)
      noktalar.add(a!)
      noktalar.add(b!)
    }
    const yaz = (i: number) => `${Math.floor(i / n) + 1}.${(i % n) + 1}`
    return `Gizli şeklin köşeleri (satır.sütun): ${[...noktalar].sort((a, b) => a - b).map(yaz).join(' ')}`
  },
}

/* ------------------------------------------------------------------ *
 * Izgara yardımcıları
 * ------------------------------------------------------------------ */

type Nokta = [number, number]

function yonler(capraz: boolean): Nokta[] {
  const dik: Nokta[] = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ]
  const egik: Nokta[] = [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ]
  return capraz ? [...dik, ...egik] : dik
}

/** Yönden bağımsız kenar kimliği (nokta indeksleriyle). */
function kenarAdi(a: number, b: number): string {
  return a < b ? `${a}-${b}` : `${b}-${a}`
}

function kenarNoktalari(k: string): [number, number] {
  const [a, b] = k.split('-').map(Number)
  return [a!, b!]
}

/** `kutu × kutu` içinde, kenarı yinelemeyen kırık çizgi. */
function yolCiz(rng: Rng, kutu: number, kenar: number, adimlar: Nokta[]): Nokta[] | null {
  for (let deneme = 0; deneme < 200; deneme++) {
    const bas: Nokta = [rng.int(0, kutu - 1), rng.int(0, kutu - 1)]
    const yol: Nokta[] = [bas]
    const kullanilan = new Set<string>()
    let kacti = false
    for (let i = 0; i < kenar; i++) {
      const [r, c] = yol[yol.length - 1]!
      const secenek = rng.shuffle(adimlar).find(([dr, dc]) => {
        const nr = r + dr
        const nc = c + dc
        if (nr < 0 || nc < 0 || nr >= kutu || nc >= kutu) return false
        return !kullanilan.has(kenarAdi(r * kutu + c, nr * kutu + nc))
      })
      if (!secenek) {
        kacti = true
        break
      }
      const hedef: Nokta = [r + secenek[0], c + secenek[1]]
      kullanilan.add(kenarAdi(r * kutu + c, hedef[0] * kutu + hedef[1]))
      yol.push(hedef)
    }
    if (!kacti) return normalize(yol)
  }
  return null
}

/** Yolu sol üst köşeye yaslar: yerleştirme hep sıfırdan başlasın. */
function normalize(yol: Nokta[]): Nokta[] {
  const minR = Math.min(...yol.map(([r]) => r))
  const minC = Math.min(...yol.map(([, c]) => c))
  return yol.map(([r, c]) => [r - minR, c - minC] as Nokta)
}

/**
 * Karenin sekiz simetrisi.
 *
 * Dördü dönme, dördü aynalama. Teklik denetiminin tabanı bu: hedefin
 * döndürülmüş ya da aynalanmış hali de "aynı şekil" sayılıyor, çünkü
 * çocuk için öyle.
 */
const SIMETRILER: ((p: Nokta) => Nokta)[] = [
  ([r, c]) => [r, c],
  ([r, c]) => [c, -r],
  ([r, c]) => [-r, -c],
  ([r, c]) => [-c, r],
  ([r, c]) => [r, -c],
  ([r, c]) => [-c, -r],
  ([r, c]) => [-r, c],
  ([r, c]) => [c, r],
]

/**
 * Hedefin `n × n` ızgaraya SIĞAN bütün yerleşimleri, kenar kümesi
 * olarak. Aynı kenar kümesini veren yerleşimler bir kez sayılıyor:
 * simetrik bir hedef iki simetri altında aynı çizime düşüyor ve iki
 * ayrı yerleşim sayılırsa teklik denetimi yanlış sonuç veriyor.
 */
function tumYerlesimler(hedef: Nokta[], n: number): string[][] {
  const gorulen = new Map<string, string[]>()
  for (const sim of SIMETRILER) {
    const d = normalize(hedef.map(sim))
    const yuk = Math.max(...d.map(([r]) => r)) + 1
    const gen = Math.max(...d.map(([, c]) => c)) + 1
    for (let dr = 0; dr + yuk <= n; dr++) {
      for (let dc = 0; dc + gen <= n; dc++) {
        const idx = d.map(([r, c]) => (r + dr) * n + (c + dc))
        const kenarlar: string[] = []
        for (let i = 1; i < idx.length; i++) kenarlar.push(kenarAdi(idx[i - 1]!, idx[i]!))
        const imza = [...kenarlar].sort().join('|')
        if (!gorulen.has(imza)) gorulen.set(imza, kenarlar)
      }
    }
  }
  return [...gorulen.values()]
}

/** Hedef, desenin içinde kaç FARKLI yerde var. */
function kacKez(hedef: Nokta[], n: number, desen: Set<string>): number {
  let sayi = 0
  for (const yerlesim of tumYerlesimler(hedef, n)) {
    if (yerlesim.every((k) => desen.has(k))) sayi++
  }
  return sayi
}

/** Izgaradaki bütün olası kenarlar. */
function tumKenarlar(n: number, adimlar: Nokta[]): string[] {
  const out = new Set<string>()
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      for (const [dr, dc] of adimlar) {
        const nr = r + dr
        const nc = c + dc
        if (nr < 0 || nc < 0 || nr >= n || nc >= n) continue
        out.add(kenarAdi(r * n + c, nr * n + nc))
      }
    }
  }
  return [...out]
}
